import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import Router, { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { useTrackDepletedActionBlocked, useTrackDepletedUpsellClicked } from '@air/analytics';
import { type ClipsListResponse } from '@air/api';
import { type Clip } from '@air/api/types';
import { FileStatus } from '@air/feature-file-status/constants';
import {
  FileStatusIcon,
  FileStatusTrackingPanel,
  type FileTrackingItem,
  type FileTrackingItemCounts,
} from '@air/feature-file-status/ui';
import { Banner } from '@air/primitive-banner';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { THREE_SECONDS } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { GalleryItemType } from '~/components/Gallery/types';
import { ImportCancellationModal } from '~/components/Import/ImportCancellationModal';
import { useImportManagerSession } from '~/components/Import/useImportManagerSession';
import { useImportPaneDismissLocalTasks } from '~/components/Import/useImportPaneDismissLocalTasks';
import {
  convertToFileTrackingItem,
  getImportHeaderTitles,
  type ImporterTaskEnhanced,
  normalizeLocalImport,
  normalizeRemoteImport,
} from '~/components/Import/utils';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { GALLERY_ASSETS } from '~/constants/react-query-keys';
import { isSubscriptionCheckoutRoute, Routes } from '~/constants/routes';
import { useCleanupImportPlaceholders } from '~/hooks/useCleanupImportPlaceholders';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { type LocationBoard } from '~/store/router/types';
import { removeTasksAction } from '~/store/tasks/actions';
import { getAdImportDescriptionMetadata } from '~/store/tasks/utils';
import { useInvalidateAdMetricSources } from '~/swr-hooks/ads/meta/useInvalidateAdMetricSources';
import { getImportsKey } from '~/swr-hooks/importer/useImportTasks';
import { useResumeImportMutation } from '~/swr-hooks/importer/useResumeImportMutation';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { ReadyState } from '~/types/sockets';
import { getPlaceholdersForTask } from '~/utils/importPlaceholderTracking';
import { useAddAssetsToAllViews, useRefreshAssetsInAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { useRefreshBoardsInAllViews } from '~/utils/mutateUtils/GalleryBoards';
import { useRefreshSideNavBoards } from '~/utils/mutateUtils/SideNavBoards';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

const reportImportManagerError = (context: string) => (error: unknown) => {
  reportErrorToBugsnag({
    context,
    error,
  });
};

export const ImportManager = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const { currentWorkspace } = useCurrentWorkspace();
  const currentBoardId = useURLBoardIdSelector();
  const { invalidateAdMetricSources } = useInvalidateAdMetricSources();
  const { cleanupPlaceholders } = useCleanupImportPlaceholders();
  const { refreshBoardsInAllViews } = useRefreshBoardsInAllViews();
  const { addAssetsToAllViews } = useAddAssetsToAllViews();
  const { refreshAssetsInAllViews } = useRefreshAssetsInAllViews();
  const queryClient = useQueryClient();
  const { refreshSideNavSubBoards, refreshRootSideNavBoards } = useRefreshSideNavBoards();
  const dispatch = useDispatch();
  const { showToast } = useToasts();

  const [isImportPaneVisible, setImportPaneVisibility] = useState(false);
  const { goToBoardPage } = useGoToPrivateBoardPage();
  const { hasImportsInProgress, hasStoppedImports, importerTasks, imports, stoppedImports, tasksSelector } =
    useImportManagerSession();
  const { dismissCompletedAndErroredLocalImportTasks } = useImportPaneDismissLocalTasks({
    localTasks: importerTasks,
  });

  const { mutation: resumeImportMutation } = useResumeImportMutation();

  const [showImportCancellationModal] = useAirModal(ImportCancellationModal);

  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canUpgradePlan = canManageSubscription(permissions);

  const [showPlansModal] = useAirModal(PlansModal);
  const { data: storage } = useStorage();
  const { data: subscription } = useSubscriptionContext();
  const { isStoragePlanRestricted } = useStoragePlanRestriction();
  const router = useRouter();
  const { trackDepletedActionBlocked } = useTrackDepletedActionBlocked();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const hasTrackedImportBlocked = useRef<boolean>(false);
  const syncExecutedForReadyStateRef = useRef<boolean>();
  const delayedRefreshTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
  const prevImporterTaskCountRef = useRef(0);
  const prevHasImportsInProgressRef = useRef<boolean>(false);

  const closePane = useCallback(() => {
    setImportPaneVisibility(false);
    dismissCompletedAndErroredLocalImportTasks();
  }, [dismissCompletedAndErroredLocalImportTasks]);

  const onRowClick = useCallback(
    (board: LocationBoard) => {
      if (board.id) {
        goToBoardPage({
          board,
          trackLocation: 'import-popover',
        }).catch(reportImportManagerError('Failed to open import board'));
      } else {
        Router.push(Routes.media.all).catch(reportImportManagerError('Failed to open all media'));
      }
    },
    [goToBoardPage],
  );

  const onCancelImport = useCallback(
    (item: FileTrackingItem) => {
      showImportCancellationModal({
        importId: item.id,
      });
    },
    [showImportCancellationModal],
  );

  const onResumeImport = useCallback(
    async (item: FileTrackingItem) => {
      const localTask = importerTasks.find((task) => task.localTaskId === item.id);

      if (localTask?.remoteTaskId) {
        await resumeImportMutation.mutateAsync([localTask.remoteTaskId]);
      } else {
        showToast('Import not found.', { color: 'red' });
      }
    },
    [resumeImportMutation, importerTasks, showToast],
  );

  const handleResumeAll = useCallback(() => {
    resumeImportMutation.mutate(stoppedImports.map(({ id }) => id));
  }, [resumeImportMutation, stoppedImports]);

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'Importer',
    remoteType: 'Importer',
    onComplete: async ({ localTask }) => {
      const parentBoardId = localTask.metadata.parentBoardId;

      if (currentBoardId && parentBoardId === currentBoardId) {
        refreshBoardsInAllViews(currentBoardId).catch(
          reportImportManagerError('Failed to refresh boards after import'),
        );
      }

      if (!currentBoardId || currentBoardId === parentBoardId) {
        await refreshAssetsInAllViews({ parentBoardId });

        const tracked = getPlaceholdersForTask(localTask.localTaskId);

        if (tracked) {
          // After the refetch, check whether the placeholder clips are still in the
          // gallery cache. The refetch replaces the locally-mutated cache with fresh
          // backend data, which removes our placeholder clips. If the real clips have
          // not been indexed yet either, re-inject the placeholders so there is no
          // empty-slot gap while waiting for the delayed retry.
          const allCachedData = queryClient.getQueriesData<InfiniteData<ClipsListResponse<Clip>>>({
            queryKey: [GALLERY_ASSETS],
          });
          const allCachedClips = allCachedData.flatMap(
            ([, data]) => data?.pages.flatMap((page) => page.data.clips) ?? [],
          );
          const placeholderIdSet = new Set(tracked.clipIds);
          const anyPlaceholderInCache = allCachedClips.some((c) => placeholderIdSet.has(c.id));

          if (!anyPlaceholderInCache) {
            // Placeholders were replaced by the refetch. Check whether the real clips
            // arrived. The backend strips the file extension from importedName, so we
            // compare after stripping.
            const strippedImportedNames = new Set(
              tracked.clips.map((c) => c.importedName?.replace(/\.[^.]+$/, '')).filter((n): n is string => !!n),
            );
            const anyRealClipInCache = allCachedClips.some(
              (c) => c.importedName && strippedImportedNames.has(c.importedName),
            );

            if (!anyRealClipInCache) {
              addAssetsToAllViews({
                assets: tracked.clips,
                parentBoardId: tracked.parentBoardId,
                type: GalleryItemType.asset,
              }).catch(reportImportManagerError('Failed to restore import placeholders'));
            }
          }

          // The backend may not have indexed all clips by the time the first
          // refetch completes. A short delayed retry catches stragglers.
          const delayedRefreshTimer = setTimeout(async () => {
            delayedRefreshTimersRef.current.delete(delayedRefreshTimer);
            await refreshAssetsInAllViews({ parentBoardId });
            Promise.resolve(cleanupPlaceholders(localTask.localTaskId)).catch(
              reportImportManagerError('Failed to clean up import placeholders'),
            );
          }, THREE_SECONDS);
          delayedRefreshTimersRef.current.add(delayedRefreshTimer);
        }
      }

      if (currentWorkspace && !parentBoardId) {
        refreshRootSideNavBoards(currentWorkspace.id).catch(
          reportImportManagerError('Failed to refresh root boards after import'),
        );
      } else if (parentBoardId) {
        Promise.resolve(refreshSideNavSubBoards(parentBoardId)).catch(
          reportImportManagerError('Failed to refresh sub-boards after import'),
        );
      }

      if (localTask.metadata.source === 'meta') {
        const metadata = getAdImportDescriptionMetadata(localTask.metadata);

        if (metadata) {
          invalidateAdMetricSources(metadata.id);
        }
      }

      queryClient.setQueriesData<{ data: ImporterTaskEnhanced[] }>(
        {
          queryKey: getImportsKey({ workspaceId: currentWorkspace?.id }),
        },
        (old) =>
          old
            ? produce(old, (draft) => {
                const oldTask = draft.data.find((item) => item.id === localTask.remoteTaskId);

                if (oldTask) {
                  oldTask.status = 'succeeded';
                }
              })
            : undefined,
      );
    },
    onUpdate: async ({ task, enrichMetadata }) => {
      if (task.data) {
        enrichMetadata(task.data);
      }
    },
    onError: ({ localTask }) => {
      Promise.resolve(cleanupPlaceholders(localTask.localTaskId)).catch(
        reportImportManagerError('Failed to clean up import placeholders'),
      );
    },
  });

  /**
   * On mount, take the tasks from local storage and add them to Redux
   */
  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  /**
   * Reset sync flag when task subscriptions become not ready
   */
  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  /**
   * Sync tasks when WebSocket reconnects to catch any missed completion events.
   * Only sync when task subscriptions are confirmed ready to avoid race conditions.
   */
  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  /**
   * Also sync when task subscriptions become ready after reconnection.
   */
  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  /**
   * When this panel unmounts (user logs out most likely),
   * reset the state
   */
  useUnmount(() => {
    delayedRefreshTimersRef.current.forEach(clearTimeout);
    delayedRefreshTimersRef.current.clear();
    dispatch(
      removeTasksAction({
        taskIds: importerTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  const inProgressImportItems =
    importerTasks.map((item) =>
      convertToFileTrackingItem({
        item: normalizeLocalImport(item),
        onRowClick,
        subscription,
        isOutOfStorage: isStoragePlanRestricted,
        includeErrorInSummary: true,
      }),
    ) || [];
  const completedImportItems =
    imports
      .filter(({ id }) => !importerTasks.some((task) => task.remoteTaskId === id))
      .map((item) =>
        convertToFileTrackingItem({
          item: normalizeRemoteImport(item),
          onRowClick,
          subscription,
          isOutOfStorage: isStoragePlanRestricted,
          includeErrorInSummary: false,
        }),
      ) || [];
  const importItems = [...inProgressImportItems, ...completedImportItems];

  // Auto-open the legacy import pane when a new import starts. Local task count and
  // remote hasImportsInProgress both matter: local tasks update on createTask dispatch,
  // while remote data may lag. closePane drops completed and errored tasks (count goes
  // down), so deps must not treat any session activity as a reopen — only a new start.
  // ui2026 does not auto-open; the rail overlay opens from the nav item.
  // biome-ignore lint/correctness/useExhaustiveDependencies(isImportPaneVisible): reopen only when an import starts, not when the user closes the pane
  useEffect(() => {
    if (ui2026) {
      return;
    }

    const importerTaskCount = importerTasks.length;
    const newImportStarted =
      importerTaskCount > prevImporterTaskCountRef.current ||
      (hasImportsInProgress && !prevHasImportsInProgressRef.current);

    prevImporterTaskCountRef.current = importerTaskCount;
    prevHasImportsInProgressRef.current = hasImportsInProgress;

    if (newImportStarted && !isImportPaneVisible) {
      setImportPaneVisibility(true);
    }
  }, [hasImportsInProgress, importerTasks.length, ui2026]);

  const handleImportBannerUpgradeClick = useCallback(() => {
    if (creditAnalyticsParams) {
      trackDepletedUpsellClicked({
        surface: 'banner',
        ...creditAnalyticsParams,
      });
    }
    showPlansModal();
  }, [creditAnalyticsParams, showPlansModal, trackDepletedUpsellClicked]);

  const handleStorageLimitUpgradeClick = useCallback(() => {
    showPlansModal();
  }, [showPlansModal]);

  // Guard prevents duplicate events; ref resets when imports resume so future blocks are tracked
  useEffect(() => {
    if (hasStoppedImports && subscription?.isCreditModel && creditAnalyticsParams && !hasTrackedImportBlocked.current) {
      hasTrackedImportBlocked.current = true;
      trackDepletedActionBlocked({
        action: 'import',
        ...creditAnalyticsParams,
      });
    } else if (!hasStoppedImports) {
      hasTrackedImportBlocked.current = false;
    }
  }, [hasStoppedImports, subscription?.isCreditModel, creditAnalyticsParams, trackDepletedActionBlocked]);

  const getImportSummary = useCallback(
    (params: { erroredCount: number; progressingCount: number; pausedCount: number; stoppedCount: number }) => {
      const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);

      if (isUpgradingPlan) {
        return null;
      }

      return params.stoppedCount && (subscription?.plan === 'basic' || isStoragePlanRestricted) ? (
        <Banner
          className="text-12"
          appearance="warning"
          suffix={
            canUpgradePlan && (
              <Button
                className="mt-1 font-medium text-inherit underline hover:underline"
                appearance="ghost"
                size="small"
                onClick={subscription?.isCreditModel ? handleImportBannerUpgradeClick : handleStorageLimitUpgradeClick}
              >
                Upgrade
              </Button>
            )
          }
        >
          {subscription?.isCreditModel
            ? "You're out of credits, imports stopped until your credits refresh or you upgrade your plan."
            : `You've hit your ${storage?.maxGb}GB import limit for free plans. Upgrade to continue.`}
        </Banner>
      ) : null;
    },
    [
      canUpgradePlan,
      handleImportBannerUpgradeClick,
      handleStorageLimitUpgradeClick,
      storage?.maxGb,
      subscription?.plan,
      router.pathname,
      isStoragePlanRestricted,
      subscription?.isCreditModel,
    ],
  );

  const getImportHeaderTitle = useCallback(
    (counts: FileTrackingItemCounts, status: FileStatus) => {
      const titles = getImportHeaderTitles(counts);
      const isInFreePlan = subscription?.isInFreePlan;
      const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);
      const isUpgradeSuccess = isUpgradingPlan && subscription?.plan === router.query.planId;

      // If the user is in a free plan and is upgrading, we want to show the upgrade in progress status
      if (isInFreePlan && isUpgradingPlan && counts.stoppedCount > 0) {
        return {
          title: 'Upgrade in progress',
          subtitle: 'Your import will restart after upgrade',
        };
      }

      // If the upgrade is successful, we want to show the success status
      if (!isInFreePlan && isUpgradeSuccess && counts.stoppedCount > 0) {
        return {
          title: 'Upgrade successful',
          subtitle: 'Import ready to restart',
        };
      }

      return titles[status];
    },
    [subscription?.isInFreePlan, router.pathname, router.query.planId, subscription?.plan],
  );

  const getImportSummaryTrackingAvatar = useCallback(
    (counts: FileTrackingItemCounts, status: FileStatus) => {
      const isInFreePlan = subscription?.isInFreePlan;
      const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);
      const isUpgradeSuccess = isUpgradingPlan && subscription?.plan === router.query.planId;

      // If the user is in a free plan and is upgrading, and there are stopped imports,
      // we want to show the progress status
      if (isInFreePlan && isUpgradingPlan && counts.stoppedCount > 0) {
        return <FileStatusIcon status={FileStatus.preparing} />;
      }

      // If the upgrade is successful, we want to show the completed status
      if (!isInFreePlan && isUpgradeSuccess && counts.stoppedCount > 0) {
        return <FileStatusIcon status={FileStatus.completed} />;
      }

      return <FileStatusIcon status={status} />;
    },
    [subscription?.isInFreePlan, router.pathname, router.query.planId, subscription?.plan],
  );

  const showResumeAllButton = useMemo(() => {
    return hasStoppedImports && !subscription?.isInFreePlan && subscription?.pricingModel !== 'credit';
  }, [hasStoppedImports, subscription?.isInFreePlan, subscription?.pricingModel]);

  // ui2026 renders this session in the imports rail. Sync stays here so the pane can close.
  if (ui2026 || !isImportPaneVisible) {
    return null;
  }

  return (
    <div className="mt-3">
      <FileStatusTrackingPanel
        getStatusTitle={getImportHeaderTitle}
        getSummaryTrackingAvatar={getImportSummaryTrackingAvatar}
        testId="IMPORTER_PANE"
        isExpanded
        disableCancel
        onDismiss={closePane}
        onCancelItem={onCancelImport}
        onResumeItem={onResumeImport}
        onResumeAll={showResumeAllButton ? handleResumeAll : undefined}
        items={importItems}
        getSummary={getImportSummary}
      />
    </div>
  );
});

ImportManager.displayName = 'ImportManager';
